import { useEffect, useState, type FormEvent } from 'react';
import { CheckCircle2, Copy, ExternalLink, Inbox, Mail, Send, TriangleAlert } from 'lucide-react';
import { adminApi, type ConnectionsStatus } from '../../lib/adminApi';

type BackendSystem = {
  id: string;
  name: string;
  provider: string;
  paid: boolean;
  purpose: string;
  status?: 'email' | 'media';
  links: Array<{ label: string; href: string }>;
  steps: string[];
  env?: string[];
};

const SYSTEMS: BackendSystem[] = [
  {
    id: 'hosting', name: 'Website hosting & database', provider: 'Fly.io', paid: true,
    purpose: 'Runs the live website and stores all your data — applications, emails, and content.',
    links: [
      { label: 'Open dashboard', href: 'https://fly.io/dashboard' },
      { label: 'Billing & plan', href: 'https://fly.io/dashboard/personal/billing' },
      { label: 'Create your own account', href: 'https://fly.io/app/sign-up' },
    ],
    steps: [
      'Create your own Fly.io account and add a payment method.',
      'Have your developer add you to the app’s Fly organization, or redeploy the app under your account.',
      'Once it’s billing to your card, remove the old payment method. To cancel entirely, delete the app and its volume in the Fly dashboard.',
    ],
  },
  {
    id: 'email', name: 'Email — sending & receiving', provider: 'Resend', paid: true, status: 'email',
    purpose: 'Sends replies & newsletters, and receives family emails into your Inbox.',
    links: [
      { label: 'Open dashboard', href: 'https://resend.com/overview' },
      { label: 'API keys', href: 'https://resend.com/api-keys' },
      { label: 'Domains', href: 'https://resend.com/domains' },
      { label: 'Billing & cancel', href: 'https://resend.com/settings/billing' },
      { label: 'Create your own account', href: 'https://resend.com/signup' },
    ],
    steps: [
      'Create your own Resend account and verify your domain (add the DNS records Resend shows).',
      'Create an API key, then have your developer update the RESEND_* values below.',
      'Send a test email above to confirm it works, then cancel the old Resend account.',
    ],
    env: ['RESEND_API_KEY', 'RESEND_FROM_EMAIL', 'RESEND_WEBHOOK_SECRET', 'RESEND_INBOUND_DOMAIN', 'RESEND_INBOUND_EMAIL', 'BREEDER_EMAIL'],
  },
  {
    id: 'media', name: 'Photo & image storage', provider: 'Cloudflare R2', paid: true, status: 'media',
    purpose: 'Stores every dog photo and image you upload in the editor.',
    links: [
      { label: 'Open R2 dashboard', href: 'https://dash.cloudflare.com/?to=/:account/r2/overview' },
      { label: 'Billing & cancel', href: 'https://dash.cloudflare.com/?to=/:account/billing' },
      { label: 'Create your own account', href: 'https://dash.cloudflare.com/sign-up' },
    ],
    steps: [
      'Create your own Cloudflare account, an R2 bucket, and enable its public URL.',
      'Create an R2 API token (Object Read & Write), then have your developer update the R2_* values below.',
      'Copy your existing images into the new bucket, then cancel the old account.',
    ],
    env: ['R2_ACCOUNT_ID', 'R2_ACCESS_KEY_ID', 'R2_SECRET_ACCESS_KEY', 'R2_BUCKET', 'R2_PUBLIC_URL'],
  },
  {
    id: 'domain', name: 'Domain name & DNS', provider: 'Domain registrar', paid: true,
    purpose: 'Your web address and where it points on the internet.',
    links: [
      { label: 'Vercel domains', href: 'https://vercel.com/dashboard/domains' },
      { label: 'Cloudflare registrar', href: 'https://dash.cloudflare.com/?to=/:account/domains' },
    ],
    steps: [
      'Ask your developer which registrar the domain is on and for an authorization (EPP/transfer) code.',
      'Start a domain transfer into your own registrar account — or simply take over billing there and update the card.',
      'Keep the DNS records pointing at Fly.io so the site stays live.',
    ],
  },
  {
    id: 'code', name: 'Source code & auto-deploy', provider: 'GitHub', paid: false,
    purpose: 'Holds the website’s code and automatically deploys updates.',
    links: [
      { label: 'Open repository', href: 'https://github.com/yesimsippingmatcha-del/sugarloaf-remastered' },
      { label: 'Create your own account', href: 'https://github.com/signup' },
    ],
    steps: [
      'Create your own GitHub account.',
      'Have your developer transfer the repository to you (repo Settings → Transfer ownership).',
      'Re-add the deploy token (FLY_API_TOKEN) as a repository secret under your account.',
    ],
  },
];

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (next.length < 8) { setNote('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setNote('New passwords do not match.'); return; }
    setBusy(true); setNote('');
    try {
      await adminApi.setPassword(current, next);
      setNote('✓ Password updated.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (error) {
      setNote(adminApi.isAuthError(error) ? 'Current password is incorrect.' : 'Could not update the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="cms-card" onSubmit={submit}>
      <h2>Change login password</h2>
      <p>Update the password you use to sign in to this dashboard.</p>
      <label className="cms-field"><span>Current password</span><input type="password" autoComplete="current-password" value={current} onChange={(event) => setCurrent(event.target.value)} /></label>
      <label className="cms-field"><span>New password</span><input type="password" autoComplete="new-password" value={next} onChange={(event) => setNext(event.target.value)} /></label>
      <label className="cms-field"><span>Confirm new password</span><input type="password" autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} /></label>
      <button type="submit" className="cms-btn cms-btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Update password'}</button>
      {note && <p className="cms-reply-note">{note}</p>}
    </form>
  );
}

function ResendSettings({ webhookUrl }: { webhookUrl: string }) {
  const [status, setStatus] = useState<Awaited<ReturnType<typeof adminApi.getEmailStatus>> | null>(null);
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    adminApi.getEmailStatus().then((result) => {
      setStatus(result);
      setTo(result.breederEmail || '');
    }).catch(() => setNote('Could not read the current Resend configuration.'));
  }, []);

  const sendTest = async (event: FormEvent) => {
    event.preventDefault();
    if (!to.trim()) return;
    setBusy(true);
    setNote('');
    try {
      const result = await adminApi.sendTestEmail(to.trim());
      setNote(result.delivered ? `Test email sent to ${result.to}.` : 'Resend is not configured, so no email was delivered.');
    } catch (error) {
      setNote(error instanceof Error ? error.message : 'The test email could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  const copyWebhook = async () => {
    await navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const inboundAddressReady = Boolean(status?.inboundConfigured && status.inboundAddress);
  const forwardingReady = Boolean(status?.forwardingTo);
  const inboundReady = inboundAddressReady && forwardingReady;
  const fullyConnected = Boolean(status?.outboundConfigured && inboundReady);

  const receivingSummary = !status
    ? 'Checking inbound setup…'
    : inboundReady
      ? `${status.inboundAddress} · CMS + ${status.forwardingTo}`
      : !status.inboundAddress
        ? 'Receiving domain is not configured'
        : !status.inboundConfigured
          ? `${status.inboundAddress} · webhook setup incomplete`
          : 'CMS receiving is ready · Gmail forwarding address is missing';

  return (
    <section className="cms-card cms-email-setup">
      <header className="cms-email-setup-head">
        <span className="cms-email-icon"><Mail size={18} /></span>
        <div><h2>Resend email</h2><p>Transactional email, family replies, newsletters, and inbound conversations.</p></div>
        {status ? <span className={`cms-connection ${fullyConnected ? 'is-connected' : 'is-missing'}`}>{fullyConnected ? <CheckCircle2 size={14} /> : <TriangleAlert size={14} />}{fullyConnected ? 'Sending + receiving' : status.outboundConfigured ? 'Receiving setup needed' : 'Setup required'}</span> : <span className="cms-connection">Checking…</span>}
      </header>

      <div className="cms-email-capabilities">
        <div><Send size={16} /><span><b>Sending</b><small>{status?.outboundConfigured ? `From ${status.from} · replies to ${status.inboundAddress ?? status.breederEmail}` : 'Add RESEND_API_KEY and RESEND_FROM_EMAIL'}</small></span><i className={status?.outboundConfigured ? 'on' : ''} /></div>
        <div><Inbox size={16} /><span><b>Receiving</b><small>{receivingSummary}</small></span><i className={inboundReady ? 'on' : ''} /></div>
      </div>

      <div className={`cms-inbound-route ${!status ? 'is-checking' : inboundReady ? 'is-live' : 'is-missing'}`}>
        <div className="cms-inbound-route-head">
          <div>
            <Inbox size={16} />
            <span><b>{!status ? 'Checking inbound delivery…' : inboundReady ? 'Inbound delivery is live' : 'Inbound delivery is not live yet'}</b><small>{!status ? 'Reading the current Resend configuration' : inboundReady ? 'One message, two destinations' : 'Complete the missing Resend settings before sharing this address'}</small></span>
          </div>
          {!status ? <span className="cms-connection">Checking…</span> : <span className={`cms-connection ${inboundReady ? 'is-connected' : 'is-missing'}`}>{inboundReady ? <CheckCircle2 size={14} /> : <TriangleAlert size={14} />}{inboundReady ? 'Live' : 'Incomplete'}</span>}
        </div>

        <div className="cms-inbound-address">
          <span>Receive mail at</span>
          {!status ? <em>Checking…</em> : status.inboundAddress ? <code>{status.inboundAddress}</code> : <em>No inbound address available</em>}
        </div>

        {!status ? (
          <p>Reading the receiving address, CMS webhook, and Gmail forwarding destination.</p>
        ) : inboundReady ? (
          <p>Every email sent to this address is saved in <b>CMS → Inbox</b> and automatically forwarded to <b>{status?.forwardingTo}</b>.</p>
        ) : (
          <p>
            {!status?.inboundAddress
              ? 'Configure RESEND_INBOUND_DOMAIN to create the receiving address. '
              : !status.inboundConfigured
                ? 'Configure RESEND_WEBHOOK_SECRET so incoming messages can be verified and saved in the CMS. '
                : ''}
            {!forwardingReady
              ? 'Set the Gmail forwarding destination before inbound delivery is considered complete.'
              : `When setup is complete, every message will be saved in CMS → Inbox and forwarded to ${status?.forwardingTo}.`}
          </p>
        )}
      </div>

      <div className="cms-email-config-grid">
        <form onSubmit={sendTest}>
          <label className="cms-field"><span>Send a test email</span><input type="email" value={to} onChange={(event) => setTo(event.target.value)} placeholder="you@example.com" /></label>
          <button type="submit" className="cms-btn cms-btn-primary" disabled={busy || !to.trim() || !status?.outboundConfigured}><Send size={14} />{busy ? 'Sending…' : 'Send test'}</button>
        </form>
        <div className="cms-webhook-inline">
          <span>Inbound webhook URL</span>
          <code>{webhookUrl}</code>
          <button type="button" className="cms-btn cms-btn-ghost" onClick={() => void copyWebhook()}><Copy size={14} />{copied ? 'Copied' : 'Copy'}</button>
        </div>
      </div>
      {note && <p className="cms-email-note" role="status">{note}</p>}
    </section>
  );
}

function statusBadge(system: BackendSystem, data: ConnectionsStatus | null) {
  if (!system.status || !data) return null;
  const ok = system.status === 'email' ? data.email.configured : data.media.configured;
  const detail = system.status === 'media' && data.media.bucket ? ` · ${data.media.bucket}` : '';
  return (
    <span className={`cms-connection ${ok ? 'is-connected' : 'is-missing'}`}>
      {ok ? <CheckCircle2 size={13} /> : <TriangleAlert size={13} />}{ok ? `Connected${detail}` : 'Setup needed'}
    </span>
  );
}

function Connections() {
  const [data, setData] = useState<ConnectionsStatus | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.getConnections().then(setData).catch(() => setError('Could not load connection status.'));
  }, []);

  const migrated = data?.migrated ?? [];
  const toggle = async (id: string) => {
    if (!data) return;
    const next = migrated.includes(id) ? migrated.filter((x) => x !== id) : [...migrated, id];
    setData({ ...data, migrated: next });
    try { await adminApi.saveConnections(next); } catch { setData({ ...data, migrated }); setError('Could not save that change.'); }
  };

  const paidTotal = SYSTEMS.filter((s) => s.paid).length;
  const paidDone = SYSTEMS.filter((s) => s.paid && migrated.includes(s.id)).length;

  return (
    <section className="cms-connections">
      <div className="cms-page-head cms-page-head-row">
        <div>
          <h2>Backend systems & ownership</h2>
          <p>Every paid service and account the site runs on. Move each one to your own account, then check it off. Use the links to open each provider’s dashboard, manage billing, or cancel.</p>
        </div>
        <span className="cms-conn-progress">{paidDone}/{paidTotal} paid accounts moved to you</span>
      </div>
      {error && <p className="cms-reply-note" role="alert">{error}</p>}

      <div className="cms-cards">
        {SYSTEMS.map((system) => {
          const done = migrated.includes(system.id);
          return (
            <article key={system.id} className={`cms-card cms-conn ${done ? 'is-done' : ''}`}>
              <div className="cms-conn-head">
                <div className="cms-conn-title">
                  <h3>{system.name}</h3>
                  <div className="cms-conn-tags">
                    <span className="cms-conn-provider">{system.provider}</span>
                    <span className={system.paid ? 'cms-conn-paid' : 'cms-conn-free'}>{system.paid ? 'Paid subscription' : 'Free'}</span>
                    {statusBadge(system, data)}
                  </div>
                </div>
                <label className={`cms-conn-toggle ${done ? 'is-on' : ''}`}>
                  <input type="checkbox" checked={done} onChange={() => void toggle(system.id)} />
                  <span>{done ? 'Moved to your account' : 'On developer’s account'}</span>
                </label>
              </div>
              <p className="cms-conn-purpose">{system.purpose}</p>
              <div className="cms-conn-links">
                {system.links.map((link) => (
                  <a key={link.href} className="cms-btn cms-btn-ghost" href={link.href} target="_blank" rel="noreferrer">{link.label} <ExternalLink size={12} /></a>
                ))}
              </div>
              <details className="cms-conn-steps">
                <summary>How to switch this to your own account</summary>
                <ol>{system.steps.map((step, i) => <li key={i}>{step}</li>)}</ol>
                {system.env && system.env.length > 0 && (
                  <p className="cms-conn-env"><span>Values your developer updates on Fly.io:</span> {system.env.map((name) => <code key={name}>{name}</code>)}</p>
                )}
              </details>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function Settings() {
  const webhookUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/webhooks/resend-inbound`;

  return (
    <div className="cms-page">
      <div className="cms-page-head">
        <h1>Settings</h1>
        <p>Manage your login, your email connection, and every backend service the site runs on — including how to move each account and subscription to your own name.</p>
      </div>

      <ResendSettings webhookUrl={webhookUrl} />

      <ChangePassword />

      <Connections />
    </div>
  );
}
